import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { RoadmapFeatureCard } from './RoadmapFeatureCard'
import { Button } from '@/components/ui/button'
import { Plus, Package, Sparkles } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import type { FeatureRequest } from '@/lib/services/feedback'
import type { Column } from '@/components/settings/ColumnManager'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { useTranslation } from '@/hooks/useTranslation'

interface RoadmapColumnProps {
	column: Column
	features: FeatureRequest[]
	onAddFeature: (status: string) => void
	onViewFeature: (featureId: string) => void
	isHighlighted?: boolean
	isDraggedOver?: boolean
	isReadOnly?: boolean
}

export function RoadmapColumn({
	column,
	features,
	onAddFeature,
	onViewFeature,
	isHighlighted = false,
	isDraggedOver = false,
	isReadOnly = false,
}: RoadmapColumnProps) {
	const { t } = useTranslation()
	const { setNodeRef, isOver } = useDroppable({
		id: column.id,
		data: {
			status: column.mapped_status,
		},
	})

	const isEmpty = features.length === 0
	const hasMany = features.length > 5
	
	// Combine different highlight states
	const shouldHighlight = isOver || isHighlighted || isDraggedOver

	return (
		<motion.div
			layout
			ref={setNodeRef}
			className={cn(
				'w-80 flex-shrink-0 bg-card/50 backdrop-blur-sm border rounded-xl h-fit min-h-[400px] transition-all duration-300',
				shouldHighlight && 'bg-primary/8 ring-2 ring-primary/40 shadow-xl scale-[1.03] border-primary/30',
				!shouldHighlight && 'hover:shadow-md hover:border-primary/20'
			)}>
			{/* Enhanced Header */}
			<div className={cn(
				"p-4 border-b transition-colors duration-300",
				shouldHighlight ? "border-primary/30 bg-primary/5" : "border-border/50"
			)}>
				<div className="flex items-center justify-between mb-2">
					<div className="flex items-center gap-2">
						<h3 className={cn(
							"font-semibold text-sm flex items-center gap-2 transition-colors duration-300",
							shouldHighlight ? "text-primary" : "text-foreground"
						)}>
							<div
								className={cn(
									"w-3 h-3 rounded-full transition-all duration-300",
									shouldHighlight && "ring-2 ring-primary/30 scale-110"
								)}
								style={{ backgroundColor: column.color }}
							/>
							{column.name}
						</h3>
						{hasMany && <Sparkles className="h-3 w-3 text-primary" />}
					</div>
					{!isReadOnly && (
						<Tooltip delayDuration={0}>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="sm"
									className={cn(
										"h-7 w-7 transition-colors duration-300",
										shouldHighlight 
											? "hover:bg-primary/20 text-primary" 
											: "hover:bg-primary/10"
									)}
									onClick={() => onAddFeature(column.mapped_status)}>
									<Plus className="h-3 w-3" />
								</Button>
							</TooltipTrigger>
							<TooltipContent className="mb-2">
								<p>{t('roadmapColumn.addFeatureTo', { columnName: column.name })}</p>
							</TooltipContent>
						</Tooltip>
					)}
				</div>

				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<span
							className={cn(
								'text-xs font-medium px-2 py-1 rounded-full transition-colors duration-300',
								shouldHighlight 
									? 'bg-primary/20 text-primary border border-primary/30'
									: features.length > 0
									? 'bg-primary/10 text-primary'
									: 'bg-muted text-muted-foreground'
							)}>
							{features.length} {features.length === 1 ? t('roadmapColumn.item') : t('roadmapColumn.items')}
						</span>
					</div>
					{shouldHighlight && (
						<motion.div
							initial={{ scale: 0, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							exit={{ scale: 0, opacity: 0 }}
							className="text-xs text-primary font-semibold flex items-center gap-1">
							<motion.div
								animate={{ scale: [1, 1.2, 1] }}
								transition={{ duration: 1, repeat: Infinity }}
								className="w-2 h-2 bg-primary rounded-full"
							/>
							{t('roadmapColumn.dropHere')}
						</motion.div>
					)}
				</div>
			</div>

			{/* Features List */}
			<div className="p-4 max-h-[calc(100vh-200px)] overflow-y-auto scrollbar-hide">
				<SortableContext
					items={features.map((f) => f.id)}
					strategy={verticalListSortingStrategy}>
					<div className="space-y-3">
						<AnimatePresence mode="popLayout">
							{features.map((feature, index) => (
								<motion.div
									key={feature.id}
									layout
									initial={{ opacity: 0, y: 20, scale: 0.95 }}
									animate={{ opacity: 1, y: 0, scale: 1 }}
									exit={{ opacity: 0, y: -20, scale: 0.95 }}
									transition={{
										duration: 0.2,
										delay: index * 0.05,
										layout: { duration: 0.2 },
									}}>
									<RoadmapFeatureCard
										feature={feature}
										onViewDetails={() => onViewFeature(feature.id)}
										isReadOnly={isReadOnly}
									/>
								</motion.div>
							))}
						</AnimatePresence>

						{/* Enhanced Empty State */}
						{isEmpty && (
							<motion.div
								initial={{ opacity: 0, scale: 0.9 }}
								animate={{ opacity: 1, scale: 1 }}
								transition={{ delay: 0.2 }}
								className="flex flex-col items-center justify-center py-12 px-4 text-center">
								<div className="relative mb-4">
									<div className="absolute inset-0 bg-muted/30 rounded-full blur-lg" />
									<div className="relative bg-muted/50 p-3 rounded-full">
										<Package className="h-6 w-6 text-muted-foreground" />
									</div>
								</div>
								<p className="text-sm text-muted-foreground mb-3 leading-relaxed">
									No features in
									<br />
									<span className="font-medium text-foreground">
										{column.name}
									</span>
								</p>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => onAddFeature(column.mapped_status)}
									className="text-xs hover:bg-primary/10 hover:text-primary transition-colors">
									<Plus className="h-3 w-3 mr-1" />
									Add feature
								</Button>
							</motion.div>
						)}
					</div>
				</SortableContext>
			</div>
		</motion.div>
	)
}
